export const CandidateStatusEnum = {
  NEW: "new",
  CONTACTED: "contacted",
  VETTING_SCHEDULED: "vetting_scheduled",
  VETTED: "vetted",
  SHORTLISTED: "shortlisted",
  INTERVIEWING: "interviewing",
  REFERENCE_CHECK: "reference_check",
  OFFERED: "offered",
  HIRED: "hired" as const,
  REJECTED: "rejected",
  ON_HOLD: "on_hold",
  WITHDRAWN: "withdrawn",
} as const;

export type CandidateStatusEnum =
  (typeof CandidateStatusEnum)[keyof typeof CandidateStatusEnum];

export interface ConfidenceScoreDetail {
  overall_score: number;
  confidence_tier: string;
  recommendation: string;
  components: {
    role_match: number;
    skills: number;
    achievements: number;
    penalties: number;
  };
  explanation: string;
  red_flags: string[];
  next_steps: string[];
}

export interface MyCandidateFromAPI {
  /** CandidateStatus document ID — used for PATCH /candidate-statuses/:id/status */
  id?: string;
  candidate_id: string;
  project_id: string;
  status: CandidateStatusEnum;
  previous_status?: CandidateStatusEnum;
  full_name: string;
  email?: string;
  phone?: string;
  confidence_score?: ConfidenceScoreDetail;
  cet_question_id?: string;
  source?: string;
  linkedin_profile_url?: string;
  profile_matching_summary?: string;
  avatar_url?: string;
  /** Role & experience — served by GET /candidates/my-candidates */
  current_job_title?: string | null;
  current_company?: string | null;
  /** e.g. "full-time", "contract" (employment_history_items.employment_type) */
  employment_type?: string | null;
  years_of_experience?: number | null;
  /** Candidate address */
  location?: string | null;
}

// ---------------------------------------------------------------------------
// Greeting API Types
// ---------------------------------------------------------------------------

export interface GreetingRequest {
  type: "email" | "linkedin";
  project_id: string;
  subject?: string;
  message?: string;
}

export interface GreetingResponse {
  sent: boolean;
  type: string;
  email?: string;
  linkedin_url?: string;
  suggested_message?: string;
  message: string;
}

/** Allowed status transitions enforced on the frontend. */
const ALL_STATUSES: CandidateStatusEnum[] = [
  "new",
  "contacted",
  "vetting_scheduled",
  "vetted",
  "shortlisted",
  "interviewing",
  "reference_check",
  "offered",
  "hired",
  "rejected",
  "on_hold",
  "withdrawn",
];

export const STATUS_TRANSITIONS: Record<
  CandidateStatusEnum,
  CandidateStatusEnum[]
> = {
  new: ALL_STATUSES.filter((s) => s !== "new"),
  contacted: ALL_STATUSES.filter((s) => s !== "contacted"),
  vetting_scheduled: ALL_STATUSES.filter((s) => s !== "vetting_scheduled"),
  vetted: ALL_STATUSES.filter((s) => s !== "vetted"),
  shortlisted: ALL_STATUSES.filter((s) => s !== "shortlisted"),
  interviewing: ALL_STATUSES.filter((s) => s !== "interviewing"),
  reference_check: ALL_STATUSES.filter((s) => s !== "reference_check"),
  offered: ALL_STATUSES.filter((s) => s !== "offered"),
  hired: ALL_STATUSES.filter((s) => s !== "hired"),
  rejected: ALL_STATUSES.filter((s) => s !== "rejected"),
  on_hold: ALL_STATUSES.filter((s) => s !== "on_hold"),
  withdrawn: ALL_STATUSES.filter((s) => s !== "withdrawn"),
};

// ---------------------------------------------------------------------------
// Candidate Detail Types (from GET /candidates/:id)
// ---------------------------------------------------------------------------

export interface EmploymentHistoryItem {
  id: string;
  company_name: string;
  job_title: string;
  location?: string;
  start_date: string;
  end_date?: string;
  is_current: boolean;
  description?: string;
  achievements: string[];
}

export interface EducationItem {
  id: string;
  institution_name?: string;
  degree?: string;
  field_of_study?: string;
  start_date?: string;
  end_date?: string;
  description?: string;
  gpa?: number | null;
  /** Honours / distinctions (v3 "honors" line under the degree). */
  achievements?: string | null;
}

/** GET /candidates/:id → awards[] (candidate_awards collection). */
export interface CandidateAward {
  id: string;
  title: string;
  issuer?: string;
  date?: string;
  description?: string;
}

export interface CandidateDocument {
  id: string;
  type: string;
  file_url: string;
  summary?: string;
  uploaded_at?: string;
  last_modified_at?: string;
}

export interface CandidateSkill {
  id: string;
  name: string;
  level?: string | null;
  min_years?: number;
  max_years?: number;
}

export interface ExpectedSalary {
  currency?: string;
  amount?: number;
  period?: string;
}

export interface CandidateReference {
  id?: string;
  name?: string;
  relationship?: string;
  contact?: string;
}

export interface JobPreference {
  id?: string;
  type?: string;
  value?: string;
}

export interface CandidateDetail {
  id: string;
  full_name: string;
  email?: string;
  phone?: string;
  address?: string;
  visa_required?: boolean;
  current_job_title?: string;
  linkedin_profile_url?: string;
  expected_salaries: ExpectedSalary[];
  employment_history_items: EmploymentHistoryItem[];
  education_items: EducationItem[];
  awards?: CandidateAward[];
  documents: CandidateDocument[];
  references: CandidateReference[];
  skills: CandidateSkill[];
  job_preferences: JobPreference[];
  profile_matching_summary?: string;
  /** AI-generated bio summary. Undefined if not yet generated or generation failed. */
  summary?: string;
  /** AI-generated profile summary derived from the candidate's CV (~100 tokens). */
  profile_summary?: string;
  createdAt?: string;
  updatedAt?: string;
  /**
   * Project-scoped status — returned when the API is called with a
   * project context (e.g. jd_path or project_id query param).
   * Optional because the bare GET /candidates/:id endpoint may omit it.
   */
  status?: CandidateStatusEnum;
  previous_status?: CandidateStatusEnum;
  /**
   * CandidateStatus document ID — needed for PATCH /candidate-statuses/:id/status.
   * Present when the response includes project-scoped data.
   */
  candidate_status_id?: string;
  avatar_url?: string;
  /**
   * Initial AI confidence score — returned when the API is called with project
   * context (e.g. jd_path query param). Same shape as confidence_score on the
   * list response.
   */
  initial_confidence_score?: ConfidenceScoreDetail;
  /**
   * Frozen, denormalized snapshot of this candidate captured at HM add-to-project
   * time. Returned only when jd_path query is present and the matching
   * CandidateStatus has a snapshot. When set, the project candidate detail page
   * renders this in place of live fields with an "as of" badge.
   */
  candidate_snapshot?: CandidateSnapshot;
  candidate_snapshot_at?: string;
  /** UUID of the user who created this candidate (creator field). */
  created_by?: string | null;
  /** UUID of the user whose action most recently mutated this candidate. */
  updated_by?: string | null;
  /** Origin tag for the last mutation: ingest_create | manual_edit | override_publish | confirm_merge | unipile_refresh | publish | reject_ingest (legacy: 'approve' pre-rename; 'deactivate'/'reactivate' under no-deactivate rule; 'override_ingest' renamed 2026-05-11). */
  last_update_origin?: string | null;
  /** If set, this is a shadow candidate awaiting compare-screen resolution against the referenced existing candidate. */
  duplicate_of_candidate_id?: string | null;
  /** Visibility flag. True = published & searchable; false = one of DRAFT / SHADOW / REJECTED / LEGACY_INACT (see lifecycle fields below to disambiguate). */
  is_active?: boolean;
  /** Timestamp of the first publish transition. Null for pre-publish rows. */
  first_approved_at?: string | null;
  /** Timestamp set when a candidate is rejected via the ingest reject action. Data is preserved; row hidden from active surfaces. */
  rejected_at?: string | null;
  /** UUID of the user who performed the reject action. */
  rejected_by?: string | null;
  /** Optional free-text reason supplied at reject time. */
  reject_reason?: string | null;
}

/**
 * Shape of CandidateStatus.candidate_snapshot. Matches what BE actually writes
 * in api/src/candidate-statuses/candidate-status-creator.service.ts
 * (`buildCandidateSnapshot`): the full Candidate doc nested under `candidate`,
 * plus parallel arrays for child collections. The BE field names below are
 * authoritative — DO NOT rename to match Candidate's flattened detail shape.
 */
export interface CandidateSnapshot {
  /** Frozen Candidate document at HM add-to-project time. */
  candidate?: {
    full_name?: string;
    email?: string;
    phone?: string;
    address?: string;
    current_job_title?: string;
    linkedin_profile_url?: string;
    avatar_url?: string;
    summary?: string;
    profile_summary?: string;
    [k: string]: unknown;
  };
  /** Frozen employment history. NOTE: BE key is `employment_history`, not `_items`. */
  employment_history?: EmploymentHistoryItem[];
  /** Frozen education. NOTE: BE key is `education`, not `education_items`. */
  education?: EducationItem[];
  skills?: CandidateSkill[];
  [k: string]: unknown;
}

// ---------------------------------------------------------------------------
// Manual Ingestion Types (CET upload-and-review flow)
// ---------------------------------------------------------------------------

export type IngestionDocKind = "cv" | "narrative" | "portfolio";

export type IngestionFieldSource =
  | "cv"
  | "narrative"
  | "portfolio"
  | "unipile"
  | "cet_edit";

export interface IngestCandidateResponse {
  candidate_id: string;
  is_active: boolean;
  field_provenance: Record<string, IngestionFieldSource>;
  /** Set only when force_proceed=true and a duplicate was found — points to the existing candidate that the user was warned about. */
  duplicate_of_candidate_id?: string;
}

/** Body shape returned with HTTP 409 when ingest hits a LinkedIn URL duplicate. */
export interface DuplicateLinkedinErrorBody {
  code: "DUPLICATE_LINKEDIN";
  message: string;
  existing_candidate_id: string;
  existing_owner_user_id?: string | null;
}

export interface MyIngestionItem {
  id: string;
  full_name: string;
  current_job_title?: string;
  /** Current employer, resolved by the BE from employment_history_items. */
  current_company?: string | null;
  address?: string | null;
  avatar_url?: string | null;
  linkedin_profile_url?: string;
  is_active: boolean;
  first_approved_at?: string | null;
  rejected_at?: string | null;
  reject_reason?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export type StatusColor =
  | "grey"
  | "orange"
  | "gold"
  | "pink"
  | "red"
  | "green"
  | "blue" | "slate";

export const STATUS_CONFIG: Record<
  CandidateStatusEnum,
  { label: string; color: StatusColor }
> = {
  // Tones follow the v3 status palette (see TONE_BY_LABEL in StatusPill):
  // grey = idle, orange = in motion, pink = brand/pipeline, blue = verified,
  // green = done, red = stopped.
  new: { label: "Uncontacted", color: "grey" },
  contacted: { label: "Contacted", color: "orange" },
  vetting_scheduled: { label: "Scheduled", color: "orange" },
  vetted: { label: "Vetted", color: "pink" },
  shortlisted: { label: "Shortlisted", color: "pink" },
  interviewing: { label: "Interviewing", color: "pink" },
  reference_check: { label: "Reference Check", color: "blue" },
  offered: { label: "Offered", color: "green" },
  hired: { label: "Hired", color: "blue" },
  rejected: { label: "Rejected", color: "red" },
  on_hold: { label: "On Hold", color: "red" },
  withdrawn: { label: "Withdrawn", color: "grey" },
};

export const STATUS_STYLE_MAP: Record<
  StatusColor,
  { text: string; border: string; bg: string }
> = {
  grey: {
    text: "text-status-grey-text",
    border: "border-status-grey-border",
    bg: "bg-status-grey-bg",
  },
  orange: {
    text: "text-status-orange-text",
    border: "border-status-orange-border",
    bg: "bg-status-orange-bg",
  },
  gold: {
    text: "text-status-gold-text",
    border: "border-status-gold-border",
    bg: "bg-status-gold-bg",
  },
  pink: {
    text: "text-status-pink-text",
    border: "border-status-pink-border",
    bg: "bg-status-pink-bg",
  },
  red: {
    text: "text-status-red-text",
    border: "border-status-red-border",
    bg: "bg-status-red-bg",
  },
  green: {
    text: "text-status-green-text",
    border: "border-status-green-border",
    bg: "bg-status-green-bg",
  },
  blue: {
    text: "text-status-blue-text",
    border: "border-status-blue-border",
    bg: "bg-status-blue-bg",
  },
  slate: {
    text: "text-status-slate-text",
    border: "border-status-slate-border",
    bg: "bg-status-slate-bg",
  },
};
